import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { unlockRingbackAudio, prepareAudioSessionForCapture } from "@/lib/ringback";

/* Pre-call device check.

   Flow:   /pre-call  →  user confirms camera + mic  →  "Start Matching"  →
           /matchmaking (with { videoDeviceId, audioDeviceId } in state)

   The page requests mic + cam up front so the browser's permission prompt
   fires before matchmaking. We render a live camera preview and a simple
   RMS mic level meter so the user can SEE both are working. Switching
   devices tears down the stream and re-acquires one bound to the chosen
   deviceId. Nothing talks to the matchmaking queue from this screen —
   that only starts once the user clicks the primary CTA. */

const DEFAULT_LABEL = "System default";

export default function PreCall() {
  const navigate = useNavigate();
  const location = useLocation();

  // Friend-invite mode: both the inviter and the accepting friend land here
  // with { mode: "friend", room } before joining their private room. The
  // primary CTA then becomes "Start Call" and skips matchmaking entirely.
  const friendRoom =
    location.state?.mode === "friend" ? location.state?.room || null : null;

  // Camera Off (phone dialler mode). Matchmaking calls take the preference
  // from Practice Setup (localStorage fallback so a refresh keeps it).
  // Friend calls carry the invite's call_mode in nav state; legacy friend
  // calls without it stay camera-on.
  const cameraOnPref =
    location.state?.cameraOn ??
    (friendRoom ? true : localStorage.getItem("salespit.camera_on") !== "0");
  const audioOnly = !cameraOnPref;

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const [status, setStatus] = useState("requesting"); // requesting|ready|denied|error
  const [errMsg, setErrMsg] = useState("");

  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [videoDeviceId, setVideoDeviceId] = useState("");
  const [audioDeviceId, setAudioDeviceId] = useState("");

  const [micLevel, setMicLevel] = useState(0); // 0-100

  // -------- helpers --------

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch {
        /* ignore */
      }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  };

  const startMicMeter = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(buf);
        // RMS-ish normalised to 0-100 with a very light smoothing cap.
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i];
        const avg = sum / buf.length; // 0..255
        setMicLevel((prev) =>
          Math.round(Math.max(avg * 0.7, prev - 4)) // decay for a natural look
        );
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.warn("[precall] mic meter failed", e);
    }
  };

  const acquireStream = async ({ vid, aud }) => {
    stopStream();
    // iOS: re-assert a capture-compatible audio session before EVERY mic
    // request. A ring (or any media) played earlier in this SPA session can
    // leave iOS's audio session playback-only, and getUserMedia then fails
    // with "AudioSession category is not compatible with audio capture" on
    // the retry. No-op on browsers without the Audio Session API.
    prepareAudioSessionForCapture();
    const constraints = audioOnly
      ? { video: false, audio: aud ? { deviceId: { exact: aud } } : true }
      : {
          video: vid ? { deviceId: { exact: vid } } : true,
          audio: aud ? { deviceId: { exact: aud } } : true,
        };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;
    if (videoRef.current && stream.getVideoTracks().length > 0) {
      videoRef.current.srcObject = stream;
    }
    startMicMeter(stream);
    return stream;
  };

  const refreshDeviceList = async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setCameras(list.filter((d) => d.kind === "videoinput"));
      setMics(list.filter((d) => d.kind === "audioinput"));
    } catch (e) {
      console.warn("[precall] enumerateDevices failed", e);
    }
  };

  // -------- mount --------

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("error");
        setErrMsg("Your browser doesn't support camera / microphone access.");
        return;
      }
      try {
        const stream = await acquireStream({});
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        // Lock the chosen device ids to whatever the browser picked by default
        const [vTrack] = stream.getVideoTracks();
        const [aTrack] = stream.getAudioTracks();
        setVideoDeviceId(vTrack?.getSettings?.().deviceId || "");
        setAudioDeviceId(aTrack?.getSettings?.().deviceId || "");
        await refreshDeviceList();
        setStatus("ready");
      } catch (e) {
        console.error("[precall] getUserMedia failed", e);
        if (!alive) return;
        if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
          setStatus("denied");
          setErrMsg(
            audioOnly
              ? "SalesPit needs access to your microphone to run a phone mock. Please allow access in your browser's address bar and refresh."
              : "SalesPit needs access to your camera and microphone to run a mock call. Please allow access in your browser's address bar and refresh."
          );
        } else {
          setStatus("error");
          setErrMsg(
            e?.message || "Couldn't start your camera / microphone. Try again."
          );
        }
      }
    })();
    return () => {
      alive = false;
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- device change --------

  const onPickCamera = async (id) => {
    setVideoDeviceId(id);
    try {
      await acquireStream({ vid: id, aud: audioDeviceId });
    } catch (e) {
      console.error("[precall] camera switch failed", e);
    }
  };

  const onPickMic = async (id) => {
    setAudioDeviceId(id);
    try {
      await acquireStream({ vid: videoDeviceId, aud: id });
    } catch (e) {
      console.error("[precall] mic switch failed", e);
    }
  };

  // -------- actions --------

  const handleStart = () => {
    // iOS (Safari + Chrome/WebKit) only unlocks audio inside a real user
    // gesture — arm the ringback element HERE, synchronously in the tap,
    // before any async work or navigation. Matchmaking phone searches only;
    // friend calls dial straight into the room and never ring.
    if (audioOnly && !friendRoom) unlockRingbackAudio();
    stopStream(); // free the preview so LiveKit can grab the devices
    if (friendRoom) {
      navigate("/video-call", {
        replace: true,
        state: {
          mode: "friend",
          room: friendRoom,
          videoDeviceId,
          audioDeviceId,
          cameraOn: cameraOnPref,
        },
      });
      return;
    }
    // Matchmaking path: carry the camera preference from Practice Setup
    // (falls back to localStorage so a refresh on this page keeps it).
    const cameraOn =
      location.state?.cameraOn ??
      localStorage.getItem("salespit.camera_on") !== "0";
    navigate("/matchmaking", {
      replace: true,
      state: { videoDeviceId, audioDeviceId, cameraOn },
    });
  };

  const handleBack = () => {
    stopStream();
    navigate(friendRoom ? "/friends" : "/homepage");
  };

  // -------- render --------

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[#0A0A0A] px-5 py-6 font-body text-white"
      data-testid="pre-call-page"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-10 h-[460px] w-[460px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.12),transparent_60%)] blur-3xl"
      />

      {/* top bar */}
      <div className="relative mx-auto flex max-w-5xl items-center justify-between">
        <Logo className="text-xl" />
        <button
          type="button"
          data-testid="pre-call-back-btn"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs font-semibold text-zinc-300 transition-colors hover:bg-white/[0.07]"
        >
          <ArrowLeft size={13} />
          Back
        </button>
      </div>

      <div className="relative mx-auto mt-8 max-w-5xl">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E52525]/30 bg-[#E52525]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#FF5A5A]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E52525]" />
            Pre-call check
          </span>
          <h1 className="font-heading mt-5 text-3xl font-black leading-[1.05] tracking-tighter sm:text-4xl lg:text-5xl">
            Make sure you're ready to go
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm font-light text-zinc-400 sm:text-base">
            {audioOnly
              ? "Confirm your mic is working before you dial in — your camera stays off for this mock."
              : "Confirm your camera and mic are working before you enter the pit. Your preview is only visible to you."}
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
          {/* -------- Preview (camera) / Phone panel (camera off) -------- */}
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black">
            {audioOnly ? (
              <div
                className="flex aspect-video w-full flex-col items-center justify-center bg-[#111] px-6"
                data-testid="pre-call-phone-panel"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-[#E52525]/30 bg-[#E52525]/10">
                  <Phone size={36} className="text-[#E52525]" />
                </span>
                <p className="mt-4 text-sm font-semibold text-white">
                  Camera off — phone mode
                </p>
                <p className="mt-1 max-w-xs text-center text-xs font-light leading-relaxed text-zinc-400">
                  Your camera stays disabled for this mock. Test your
                  microphone, then start matching — it'll ring like a real call.
                </p>
              </div>
            ) : (
              <div className="aspect-video w-full bg-[#111]">
                <video
                  ref={videoRef}
                  data-testid="pre-call-video"
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover [transform:scaleX(-1)]"
                />
              </div>
            )}

            {/* overlay states */}
            {(status === "requesting" || status === "denied" || status === "error") && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                {status === "requesting" && (
                  <div className="flex flex-col items-center gap-3 text-center">
                    <Loader2 size={30} className="animate-spin text-[#E52525]" />
                    <p className="text-sm text-zinc-300">
                      {audioOnly
                        ? "Waiting for mic permission…"
                        : "Waiting for camera & mic permission…"}
                    </p>
                  </div>
                )}
                {(status === "denied" || status === "error") && (
                  <div
                    className="mx-6 flex max-w-sm flex-col items-start gap-3 rounded-2xl border border-[#E52525]/30 bg-[#1a0808]/80 p-5 text-left"
                    data-testid="pre-call-permission-error"
                  >
                    <AlertTriangle size={22} className="text-[#E52525]" />
                    <p className="text-sm leading-relaxed text-zinc-200">
                      {errMsg}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* small badge in top-left */}
            {status === "ready" && !audioOnly && (
              <span className="pointer-events-none absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#22C55E]" />
                Preview
              </span>
            )}
          </div>

          {/* -------- Controls -------- */}
          <div className="flex flex-col gap-5">
            {/* Camera (hidden in phone mode — camera stays off) */}
            {!audioOnly && (
              <div className="rounded-2xl border border-white/10 bg-[#141414] p-5">
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                  {status === "ready" ? (
                    <Video size={14} className="text-[#22C55E]" />
                  ) : (
                    <VideoOff size={14} className="text-zinc-500" />
                  )}
                  Camera
                </label>
                <select
                  data-testid="pre-call-camera-select"
                  value={videoDeviceId}
                  onChange={(e) => onPickCamera(e.target.value)}
                  disabled={status !== "ready" || cameras.length === 0}
                  className="mt-3 w-full appearance-none rounded-xl border border-white/10 bg-[#0F0F0F] px-3.5 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#E52525]/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cameras.length === 0 && (
                    <option value="">{DEFAULT_LABEL}</option>
                  )}
                  {cameras.map((c, i) => (
                    <option key={c.deviceId} value={c.deviceId}>
                      {c.label || `Camera ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Mic + meter */}
            <div className="rounded-2xl border border-white/10 bg-[#141414] p-5">
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                {status === "ready" ? (
                  <Mic size={14} className="text-[#22C55E]" />
                ) : (
                  <MicOff size={14} className="text-zinc-500" />
                )}
                Microphone
              </label>
              <select
                data-testid="pre-call-mic-select"
                value={audioDeviceId}
                onChange={(e) => onPickMic(e.target.value)}
                disabled={status !== "ready" || mics.length === 0}
                className="mt-3 w-full appearance-none rounded-xl border border-white/10 bg-[#0F0F0F] px-3.5 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#E52525]/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {mics.length === 0 && (
                  <option value="">{DEFAULT_LABEL}</option>
                )}
                {mics.map((m, i) => (
                  <option key={m.deviceId} value={m.deviceId}>
                    {m.label || `Microphone ${i + 1}`}
                  </option>
                ))}
              </select>

              {/* RMS bar */}
              <div
                className="mt-4"
                data-testid="pre-call-mic-meter"
                aria-label="Microphone level"
              >
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/5">
                  <motion.span
                    className="block h-full rounded-full bg-gradient-to-r from-[#22C55E] via-[#F5A524] to-[#E52525]"
                    animate={{ width: `${Math.min(100, micLevel)}%` }}
                    transition={{ type: "spring", stiffness: 200, damping: 24 }}
                  />
                </div>
                <p className="mt-2 text-xs text-zinc-500">
                  Speak a few words — you should see the bar move.
                </p>
              </div>
            </div>

            {/* Primary CTA */}
            <motion.button
              type="button"
              data-testid={friendRoom ? "pre-call-start-call-btn" : "pre-call-start-match-btn"}
              disabled={status !== "ready"}
              onClick={handleStart}
              whileHover={status === "ready" ? { scale: 1.02 } : undefined}
              whileTap={status === "ready" ? { scale: 0.97 } : undefined}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#E52525] px-7 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.35)] transition-[background-color,box-shadow,opacity] hover:bg-[#FF3333] hover:shadow-[0_0_45px_rgba(229,37,37,0.5)] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-zinc-500 disabled:shadow-none"
            >
              <AnimatePresence mode="wait" initial={false}>
                {status === "ready" ? (
                  <motion.span
                    key="ready"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="inline-flex items-center gap-3"
                  >
                    {friendRoom ? "Start Call" : "Start Matching"}
                    <ArrowRight
                      size={19}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </motion.span>
                ) : (
                  <motion.span
                    key="pending"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="inline-flex items-center gap-2"
                  >
                    {status === "requesting" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Waiting for permission…
                      </>
                    ) : (
                      <>Fix permissions to continue</>
                    )}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>
    </main>
  );
}
